import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { staffUserCreateSchema, type StaffUserCreate } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { FieldError, Input, Label, Select } from '../../components/ui/field';
import { api } from '../../shared/api';
import { copy, labelForStaffRole } from '../../shared/i18n';

const fallbackRoles = ['Administrator', 'Receptionist', 'Staff'];

export function UserCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const rolesQuery = useQuery({ queryKey: ['staff-roles'], queryFn: api.staffRoles.list });
  const roles = rolesQuery.data?.items.length ? rolesQuery.data.items : fallbackRoles;
  const form = useForm<StaffUserCreate>({ resolver: zodResolver(staffUserCreateSchema), defaultValues: { email: '', givenName: '', familyName: '', role: 'Staff' } });
  const createUser = useMutation({
    mutationFn: api.staffUsers.create,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['staff-users'] });
      navigate('/admin/users');
    },
  });
  return (
    <div className="max-w-lg space-y-5">
      <div><h1 className="text-2xl font-semibold text-slate-950">{copy.users.invite}</h1><p className="mt-1 text-sm text-slate-500">{copy.users.body}</p></div>
      <Card><CardHeader><h2 className="text-sm font-semibold">{copy.users.inviteUser}</h2></CardHeader><CardContent>
        <form className="grid gap-3" onSubmit={form.handleSubmit((values) => createUser.mutate(values))}>
          <div className="grid gap-1.5"><Label htmlFor="email">{copy.common.email}</Label><Input id="email" type="email" {...form.register('email')} /><FieldError message={form.formState.errors.email?.message} /></div>
          <div className="grid gap-1.5"><Label htmlFor="givenName">Nombre</Label><Input id="givenName" {...form.register('givenName')} /><FieldError message={form.formState.errors.givenName?.message} /></div>
          <div className="grid gap-1.5"><Label htmlFor="familyName">Apellido</Label><Input id="familyName" {...form.register('familyName')} /><FieldError message={form.formState.errors.familyName?.message} /></div>
          <div className="grid gap-1.5"><Label htmlFor="role">{copy.common.role}</Label><Select id="role" disabled={rolesQuery.isLoading} {...form.register('role')}>{roles.map((role) => <option key={role} value={role}>{labelForStaffRole(role)}</option>)}</Select><FieldError message={form.formState.errors.role?.message} /></div>
          {createUser.isError ? <p className="text-sm text-rose-600">{createUser.error.message}</p> : null}
          <Button type="submit" disabled={createUser.isPending}><Plus className="h-4 w-4" aria-hidden="true" />{copy.users.inviteUser}</Button>
        </form>
      </CardContent></Card>
    </div>
  );
}
